// Copyright (c) Docugami, Inc. All rights reserved.

import { setEditTokenRange } from "hooks/chunkSelection";
import { TokenRange } from "networking/api";
import { IExternalCallbacks } from "sdv/ExternalCallbacks";
import { InteractEvent } from "sdv/InteractEvent";
import { Mode } from "sdv/Mode";
import { getTokenRangesForPages } from "sdv/Page";
import { IChunkResizeResult, SdvInterface } from "sdv/SdvInterface";
import { BoundsComponent } from "sdv/components/BoundsComponent";
import { BreakHyperlineComponent, getBreakHyperlineSettings } from "sdv/components/BreakHyperlineComponent";
import { ChunkRenderComponent } from "sdv/components/ChunkRenderComponent";
import { ControlComponent, ControlType, getEnclosingHyperlineForRange, getEnclosingHyperlineForToken, getTokenRange, getTokenRangeForControl, levelOrderTraversal } from "sdv/components/ControlComponent";
import { CursorComponent } from "sdv/components/CursorComponent";
import { Grabber, GrabberComponent, GrabberType } from "sdv/components/GrabberComponent";
import { InteractableComponent } from "sdv/components/InteractableComponent";
import { ResizeComponent, TokenSnapBehavior } from "sdv/components/ResizeComponent";
import { TokenLinesComponent, TokenRangeBox } from "sdv/components/TokenLinesComponent";
import { TokenRangeComponent } from "sdv/components/TokenRangeComponent";
import { TokenRangeInfoComponent } from "sdv/components/TokenRangeInfoComponent";
import { ComponentId, tagComponent } from "sdv/engine/Component";
import { Entity, EntityId, IEntityManager, InvalidEntityId } from "sdv/engine/Entity";
import { createGrabberEntity } from "sdv/engine/EntityFactory";
import { ActionId } from "sdv/input/Actions";
import { InputState } from "sdv/input/InputController";
import { createGrabbers } from "sdv/utils/Grabbers";
import { getOverlappingEditTokens, growBox, pointInBoxes } from "store/shared_types";
import { blendColors } from "utils/ColorUtils";
import { animationCurves } from "utils/Easings";
import { clamp, getDistanceBetweenPoints } from "utils/MathUtils";
import { TokenRangeUtils } from "utils/TokenRangeUtils";
import { IResources } from "../Resources";
import { ISystem, Queries } from "../engine/Engine";

/**
 * Manages resize operations on the selected entity
 */
export class ResizeSystem implements ISystem {
    private _grabberEntityId: EntityId = -1;

    private _onGetResizeResult?: () => IChunkResizeResult | null;
    private _onEndResize?: (succeeded: boolean) => void;

    get updateEnabled() { return true; }

    resources!: IResources;
    entities!: IEntityManager;
    input!: InputState;
    callbacks!: IExternalCallbacks;
    sdvInterface!: SdvInterface;
    dt!: number;

    queries: Queries = {
        selected: { types: [ComponentId.TokenRange, ComponentId.Selected, ComponentId.ChunkRender, ComponentId.TokenRangeInfo] },
        grabbers: { types: [ComponentId.Grabber, ComponentId.Interactable] },
        activeGrabbers: { types: [ComponentId.Grabber, ComponentId.Drag] },
        resizeOperation: { types: [ComponentId.Resize, ComponentId.TokenRange, ComponentId.TokenRangeInfo, ComponentId.ChunkRender] },
        cursor: { types: [ComponentId.Cursor] },
        root: { types: [ComponentId.RootControl, ComponentId.Control, ComponentId.Active] },
        tokenLines: { types: [ComponentId.TokenLines] },
        tableCells: { types: [ComponentId.TableCell, ComponentId.TokenRange] },
        tables: { types: [ComponentId.Table, ComponentId.TokenRange] },
        breakHyperline: { types: [ComponentId.BreakHyperline] },
        highlights: { types: [ComponentId.Highlighter, ComponentId.TokenRange] },
        multiSelect: { types: [ComponentId.MultiSelect] }
    }

    initialize(sdvInterface: SdvInterface) {
        this._onEndResize = this.endResize.bind(this);
        this._onGetResizeResult = this.getResizeResult.bind(this);

        sdvInterface.resize = {
            get: this._onGetResizeResult,
            end: this._onEndResize
        }
    }

    getResizeResult(): IChunkResizeResult | null {
        const resizeEntity = this.queries.resizeOperation.query?.single();
        if (!!resizeEntity) {
            const oldTokenRange = resizeEntity.getComponent<ResizeComponent>(ComponentId.Resize)!.ops[0].tokenRange;
            const newTokenRange = resizeEntity.getComponent<TokenRangeComponent>(ComponentId.TokenRange)!.tokenRange;

            return {
                id: resizeEntity.name,
                oldTokenRange: oldTokenRange,
                newTokenRange: newTokenRange
            }
        }

        return null;
    }

    cancel(): void {
        this.endResize(true);
    }

    update() {
        this.createOrDestroyGrabbers();
        this.showOrHideGrabbers();
        this.startResize();
        this.updateResizeOps();
        this.updateSnapBehaviorForGrabbers();
        this.resizeTokenHitTest();
        this.promoteSnapBehaviorTokenToHyperline();
        this.promoteSnapBehaviorHyperlineToTokenLine();
        this.updateResizeTokenRange();
        this.updateGrabberHitBoxesAndVisuals();
        this.resetResizeVisuals();
        this.updateBreakHyperlineVisuals();
        this.handleSelectionChanges();
        this.handleMultiSelect();
    }

    private updateSnapBehaviorForGrabbers() {
        const activeGrabbers = this.queries.activeGrabbers.query!.entities;
        if (activeGrabbers.length > 0) {
            const snapBehavior: TokenSnapBehavior = !!activeGrabbers.find(x => {
                const grabberComp = x.getComponent<GrabberComponent>(ComponentId.Grabber)!
                return grabberComp.grabber.type === GrabberType.Bottom || grabberComp.grabber.type === GrabberType.Top;
            }) ? TokenSnapBehavior.Hyperline : TokenSnapBehavior.Token;

            const isFromTok = activeGrabbers.length > 0 ? !!activeGrabbers.find(x => {
                const grabberComp = x.getComponent<GrabberComponent>(ComponentId.Grabber)!
                return grabberComp.grabber.type === GrabberType.Left || grabberComp.grabber.type === GrabberType.Top;
            }) : undefined;

            for (const resizeEntity of this.queries.resizeOperation.query!.entities) {
                const resizeComp = resizeEntity.getComponent<ResizeComponent>(ComponentId.Resize)!;
                resizeComp.isFromTok = isFromTok;
                resizeComp.snapBehavior = snapBehavior;
            }
        }
    }

    private promoteSnapBehaviorTokenToHyperline() {
        const resizeComp = this.queries.resizeOperation.query!.single()?.getComponent<ResizeComponent>(ComponentId.Resize)!;
        if (!resizeComp?.isResizing || resizeComp.snapBehavior !== TokenSnapBehavior.Token) {
            return;
        }

        const targetToken = resizeComp.hitResult?.token ?? null;
        if (targetToken !== null) {
            const previousResizeOp = resizeComp.ops[resizeComp.ops.length - 1];
            const previousTokenRangeInfo = previousResizeOp.info;

            if (resizeComp.isFromTok === true) {
                // We can only snap to tokens or token lines within the original hyperline so ensure that's the case
                const previousFromTokHyperlineTokenRange = getTokenRange(this.entities.get(previousTokenRangeInfo.fromTokHyperlineId ?? ''));
                if (!!previousFromTokHyperlineTokenRange && (targetToken >= previousFromTokHyperlineTokenRange.toTok || targetToken < previousFromTokHyperlineTokenRange.fromTok)) {
                    // Out of the valid token snap range, revert to hyperline snapping
                    resizeComp.snapBehavior = TokenSnapBehavior.Hyperline;
                }
            }
            else if (resizeComp.isFromTok === false) {
                // We can only snap to tokens or token lines within the original hyperline so ensure that's the case
                const previousToTokHyperlineTokenRange = getTokenRange(this.entities.get(previousTokenRangeInfo.toTokHyperlineId ?? ''));
                if (!!previousToTokHyperlineTokenRange && (targetToken >= previousToTokHyperlineTokenRange.toTok || targetToken < previousToTokHyperlineTokenRange.fromTok)) {
                    // Out of the valid token snap range, revert to hyperline snapping
                    resizeComp.snapBehavior = TokenSnapBehavior.Hyperline;
                }
            }
        }
    }

    private promoteSnapBehaviorHyperlineToTokenLine() {
        const resizeEntity = this.queries.resizeOperation.query!.single();
        const resizeComp = resizeEntity?.getComponent<ResizeComponent>(ComponentId.Resize)!;

        const breakHyperlineEntity = this.queries.breakHyperline.query!.single();
        const cursor = this.input.getAxis2d(ActionId.SelectCursor);
        if (!!resizeEntity && !!resizeComp?.isResizing && !resizeComp?.isCreating && resizeComp.snapBehavior === TokenSnapBehavior.Hyperline && !!cursor) {
            let breakHyperlineComponent = breakHyperlineEntity?.getComponent<BreakHyperlineComponent>(ComponentId.BreakHyperline)!;
            if (!breakHyperlineComponent) {
                if (!!resizeComp.hitResult?.tokenHyperlineId) {
                    breakHyperlineComponent = {
                        typeId: ComponentId.BreakHyperline,
                        settings: getBreakHyperlineSettings(),
                        startCursor: cursor,
                        accumulator: 0,
                        startHyperlineId: resizeComp.hitResult.tokenHyperlineId
                    }

                    resizeEntity.addComponent(breakHyperlineComponent);
                }
            }
            else {
                const settings = breakHyperlineComponent.settings;
                const isValid = (breakHyperlineComponent.accumulator * 1000 > settings.timeThresholdMs) && (!resizeComp.hitResult || breakHyperlineComponent.startHyperlineId === resizeComp.hitResult.tokenHyperlineId);
                const dist = getDistanceBetweenPoints(cursor, breakHyperlineComponent.startCursor);

                if (isValid) {
                    resizeComp.snapBehavior = TokenSnapBehavior.TokenLine;
                }
                else if (dist <= settings.movementThresholdPixels) {
                    breakHyperlineComponent.accumulator += this.dt;
                }
                else {
                    breakHyperlineEntity?.removeComponent(ComponentId.BreakHyperline);
                }
            }
        }
        else {
            breakHyperlineEntity?.removeComponent(ComponentId.BreakHyperline);
        }
    }

    private updateGrabberHitBoxesAndVisuals() {
        // Update the hit boxes and visuals for the grabbers
        const selectedEntity = this.queries.selected.query!.single();
        const grabbers = this.queries.grabbers.query!.entities;
        if (grabbers.length > 0 && !!selectedEntity) {
            const grabberInfo = this.createGrabbers(selectedEntity);
            for (const entity of grabbers) {
                const grabber = entity.getComponent<GrabberComponent>(ComponentId.Grabber)!;
                const interactable = entity.getComponent<InteractableComponent>(ComponentId.Interactable)!;

                const targetInfo = grabberInfo.find(g => g.type === grabber.grabber.type);
                if (!!targetInfo) {
                    grabber.grabber = targetInfo;
                    interactable.hitBox = targetInfo.bounds;
                }
            }
        }
    }

    private updateResizeOps() {
        const resizeEntity = this.queries.resizeOperation.query!.entities[0];
        if (!resizeEntity) {
            return;
        }

        const resizeComp = resizeEntity.getComponent<ResizeComponent>(ComponentId.Resize)!;
        const resizeTokenRange = resizeEntity.getComponent<TokenRangeComponent>(ComponentId.TokenRange)!;
        const tokenRangeInfo = resizeEntity.getComponent<TokenRangeInfoComponent>(ComponentId.TokenRangeInfo)!;

        // Accumulate individual resize operations throughout the user interaction
        const isResizing = this.queries.activeGrabbers.query!.any() || !!resizeComp.isCreating;
        if (resizeComp.isResizing !== isResizing) {
            if (!isResizing) {
                // Op complete
                resizeComp.ops = resizeComp.ops || [];
                resizeComp.ops.push({
                    tokenRange: { ...resizeTokenRange.tokenRange },
                    info: { ...tokenRangeInfo },
                    snapBehavior: resizeComp.snapBehavior,
                    isFromTok: resizeComp.isFromTok
                });
            }

            resizeComp.isResizing = isResizing;
        }
    }

    private resizeTokenHitTest() {
        const resizeComp = this.queries.resizeOperation.query!.single()?.getComponent<ResizeComponent>(ComponentId.Resize)!;
        if (!resizeComp?.isResizing) {
            return;
        }

        delete resizeComp.hitResult;

        const tokenLine = this.getTokenLineForCursor(!!resizeComp.isFromTok);
        if (!!tokenLine) {
            let targetToken = this.getTokenForCursorFromLine(tokenLine, !!resizeComp.isFromTok);
            if (targetToken !== null) {
                // Restrict token based on resize token range
                const previousResizeOp = resizeComp.ops[resizeComp.ops.length - 1];
                const previousTokenRange = previousResizeOp.tokenRange;
                if (resizeComp.isFromTok === true) {
                    targetToken = Math.min(targetToken, previousTokenRange.toTok - 1);
                }
                else if (resizeComp.isFromTok === false) {
                    targetToken = Math.max(targetToken, previousTokenRange.fromTok);
                }

                const tokenHyperlineId = getEnclosingHyperlineForToken(targetToken, this.queries.root.query!.single(), this.entities)?.name;
                const tokenLineHyperlineId = getEnclosingHyperlineForRange(tokenLine.tokenRange, this.queries.root.query!.single(), this.entities)?.name;

                resizeComp.hitResult = {
                    token: targetToken,
                    line: tokenLine,
                    tokenHyperlineId: tokenHyperlineId,
                    tokenLineHyperlineId: tokenLineHyperlineId
                }
            }
        }
    }

    private updateResizeTokenRange() {
        const resizeEntity = this.queries.resizeOperation.query!.entities[0];
        if (!resizeEntity) {
            return;
        }

        const resizeComp = resizeEntity.getComponent<ResizeComponent>(ComponentId.Resize)!;
        if (!resizeComp.isResizing) {
            return;
        }

        const targetToken: number | null = resizeComp.hitResult?.token ?? null;
        if (targetToken !== null) {
            const resizeTokenRange = resizeEntity.getComponent<TokenRangeComponent>(ComponentId.TokenRange)!;
            const newRange: TokenRange = { ...resizeTokenRange.tokenRange };

            const previousResizeOp = resizeComp.ops[resizeComp.ops.length - 1];
            const previousTokenRange = previousResizeOp.tokenRange;
            const previousTokenRangeInfo = previousResizeOp.info;

            if (resizeComp.isFromTok === true) {
                // Maintain the original toTok when modifying the fromTok for token snapping - this will undo any previous hyperline snapping if the user returns to a valid location
                newRange.toTok = previousTokenRange.toTok;
            }
            else if (resizeComp.isFromTok === false) {
                // Maintain the original fromTok when modifying the toTok for token snapping - this will undo any previous hyperline snapping if the user returns to a valid location
                newRange.fromTok = previousTokenRange.fromTok;
            }

            // We disable hyperline snapping for the from/toTok not associated with the active resize operation if the user has already
            // adjusted its position with a token or tokenLine operation to ensure we don't reset their progress
            const disableHyperlineSnapping = !!resizeComp.ops.find(x => x.isFromTok !== undefined && x.isFromTok !== resizeComp.isFromTok);

            switch (resizeComp.snapBehavior) {
                case TokenSnapBehavior.TokenLine: {
                    if (resizeComp.isFromTok === true) {
                        newRange.fromTok = resizeComp.hitResult!.line.tokenRange.fromTok;

                        if (!disableHyperlineSnapping) {
                            // Snap the toTok based on the containing hyperline
                            const previousHyperlineTokenRange = getTokenRange(this.entities.get(previousTokenRangeInfo.toTokHyperlineId ?? ''));
                            newRange.toTok = previousHyperlineTokenRange?.toTok ?? newRange.toTok;
                        }
                    }
                    else if (resizeComp.isFromTok === false) {
                        newRange.toTok = resizeComp.hitResult!.line.tokenRange.toTok;

                        if (!disableHyperlineSnapping) {
                            // Snap the fromTok based on the containing hyperline
                            const previousHyperlineTokenRange = getTokenRange(this.entities.get(previousTokenRangeInfo.fromTokHyperlineId ?? ''));
                            newRange.fromTok = previousHyperlineTokenRange?.fromTok ?? newRange.fromTok;
                        }
                    }

                    break;
                }

                case TokenSnapBehavior.Hyperline: {
                    const hyperlineTokenRange = getTokenRange(this.entities.get(resizeComp.hitResult?.tokenHyperlineId ?? ''));
                    if (!!hyperlineTokenRange) {
                        if (resizeComp.isFromTok === true) {
                            newRange.fromTok = hyperlineTokenRange.fromTok;
                        }
                        else if (resizeComp.isFromTok === false) {
                            newRange.toTok = hyperlineTokenRange.toTok;
                        }
                    }

                    if (!disableHyperlineSnapping) {
                        if (resizeComp.isFromTok === true) {
                            // Snap the toTok based on the containing hyperline
                            const previousHyperlineTokenRange = getTokenRange(this.entities.get(previousTokenRangeInfo.toTokHyperlineId ?? ''));
                            newRange.toTok = previousHyperlineTokenRange?.toTok ?? newRange.toTok;
                        }
                        else if (resizeComp.isFromTok === false) {
                            // Snap the fromTok based on the containing hyperline
                            const previousHyperlineTokenRange = getTokenRange(this.entities.get(previousTokenRangeInfo.fromTokHyperlineId ?? ''));
                            newRange.fromTok = previousHyperlineTokenRange?.fromTok ?? newRange.fromTok;
                        }
                    }

                    break;
                }

                case TokenSnapBehavior.Token: {
                    if (resizeComp.isFromTok === true) {
                        newRange.fromTok = targetToken;
                    }
                    else if (resizeComp.isFromTok === false) {
                        newRange.toTok = targetToken + 1;
                    }
                    break;
                }
            }

            const restrictedRange: TokenRange | null = this.getResizeRestrictedRange(resizeEntity);
            if (!!restrictedRange) {
                newRange.fromTok = Math.max(newRange.fromTok, restrictedRange.fromTok);
                newRange.toTok = Math.min(newRange.toTok, restrictedRange.toTok);
            }

            // Check tables to ensure valid resize - snap to table bounds
            let isSubTable = false;
            for (const entity of this.queries.tables.query!.entities) {
                const tokenRange = getTokenRange(entity)!;
                if (TokenRangeUtils.rangesEqual(tokenRange, newRange)) {
                    // Invalid
                    return;
                }

                if (TokenRangeUtils.rangeIsContained(newRange, tokenRange)) {
                    isSubTable = true;
                }
                else if (TokenRangeUtils.rangesOverlap(newRange, tokenRange)) {
                    newRange.fromTok = Math.min(tokenRange.fromTok, newRange.fromTok);
                    newRange.toTok = Math.max(tokenRange.toTok, newRange.toTok);
                }
            }

            // Check table cells to ensure a valid resize if within a table
            if (isSubTable) {
                let fromTokCellId: string | null = null;
                let toTokCellId: string | null = null;
                for (const entity of this.queries.tableCells.query!.entities) {
                    const tokenRange = getTokenRange(entity)!;
                    if (!fromTokCellId && newRange.fromTok >= tokenRange.fromTok && newRange.fromTok < tokenRange.toTok) {
                        fromTokCellId = entity.name;
                    }

                    if (!toTokCellId && newRange.toTok - 1 >= tokenRange.fromTok && newRange.toTok <= tokenRange.toTok) {
                        toTokCellId = entity.name;
                    }

                    if (!!toTokCellId && !!fromTokCellId) {
                        break;
                    }
                }

                if (fromTokCellId !== toTokCellId) {
                    // Invalid
                    return;
                }
            }

            // Handle the case where token bounds are shared across tokens. This occurs when tokens are edited.
            if (newRange.fromTok !== newRange.toTok) {
                const pageTokens = this.resources.get("pageTokens").value;
                const fromTokenEdits = getOverlappingEditTokens(newRange.fromTok, pageTokens);
                const toTokenEdits = newRange.fromTok === newRange.toTok - 1 ? fromTokenEdits : getOverlappingEditTokens(newRange.toTok - 1, pageTokens);
                if (!!fromTokenEdits?.length) {
                    newRange.fromTok = Math.min(newRange.fromTok, fromTokenEdits[0]);
                }

                if (!!toTokenEdits?.length) {
                    newRange.toTok = Math.max(newRange.toTok - 1, toTokenEdits[toTokenEdits.length - 1]) + 1;
                }
            }

            resizeTokenRange.tokenRange = newRange;
        }
    }

    private startResize(): void {
        const selectedEntity = this.queries.selected.query!.single();
        const activeGrabber = this.queries.activeGrabbers.query!.single();

        if (!this.resources.get("resizeDisabled").value && !!selectedEntity && !!activeGrabber) {
            const resizeOp = this.queries.resizeOperation.query!.single();
            const isResizing = resizeOp?.getComponent<ResizeComponent>(ComponentId.Resize)!.isResizing;
            if (!isResizing) {
                this.resources.set("mode", Mode.Edit);
            }
            if (!resizeOp) {
                if (!!this.callbacks.onInteraction) {
                    this.callbacks.onInteraction(InteractEvent.Resize)
                }
                const tokenRange = selectedEntity.getComponent<TokenRangeComponent>(ComponentId.TokenRange)!;
                const tokenRangeInfo = selectedEntity.getComponent<TokenRangeInfoComponent>(ComponentId.TokenRangeInfo)!;
                selectedEntity
                    .addComponent<ResizeComponent>({
                        typeId: ComponentId.Resize,
                        ops: [{ tokenRange: { ...tokenRange.tokenRange }, info: { ...tokenRangeInfo } }],
                        snapBehavior: TokenSnapBehavior.Token
                    });
            }
        }
    }

    private createOrDestroyGrabbers(): void {
        const grabberEntities = this.queries.grabbers.query!.entities;
        const selectedEntity = this.queries.selected.query!.entities[0];
        let destroyed: boolean = false;
        if (this._grabberEntityId !== InvalidEntityId) {
            if (selectedEntity?.id !== this._grabberEntityId) {
                destroyed = true;
                for (const entity of grabberEntities) {
                    this.entities.destroy(entity);
                }
            }
        }

        if ((destroyed || grabberEntities.length === 0) && !!selectedEntity) {
            if (!selectedEntity.hasComponent(ComponentId.TableChunkRender) && !selectedEntity.hasComponent(ComponentId.TableColumnRender)) {
                this._grabberEntityId = selectedEntity.id;
                this.createGrabberEntities(selectedEntity);
            }
        }
    }

    private createGrabberEntities(entity: Entity) {
        this.createGrabbers(entity).forEach(grabber => {
            createGrabberEntity(
                this.entities,
                grabber,
                () => {
                    const resizeEntity = this.queries.resizeOperation.query?.single();
                    if (!!resizeEntity) {
                        const tokenRange = resizeEntity.getComponent<TokenRangeComponent>(ComponentId.TokenRange)!.tokenRange;
                        setEditTokenRange(tokenRange);
                    }
                });
        });
    }

    private createGrabbers(entity: Entity): Grabber[] {
        let grabbers: Grabber[] = [];

        const tokenRangeInfoComp = entity.getComponent<TokenRangeInfoComponent>(ComponentId.TokenRangeInfo);
        const renderChunk = entity.getComponent<ChunkRenderComponent>(ComponentId.ChunkRender);
        if (!!tokenRangeInfoComp && !!renderChunk?.renderBoxes.length) {
            grabbers = createGrabbers(renderChunk.bounds);
        }

        return grabbers;
    }

    private showOrHideGrabbers(): void {
        const selectedEntity = this.queries.selected.query!.single();
        if (!selectedEntity) {
            return;
        }

        const cursor = this.input.getAxis2d(ActionId.SelectCursor);
        let isHovered = false;
        if (!!cursor) {
            const renderInfo = selectedEntity.getComponent<ChunkRenderComponent>(ComponentId.ChunkRender)!;
            const hitBoxes = renderInfo.renderBoxes.map(x => growBox(x.box, 15));
            isHovered = pointInBoxes(cursor.x, cursor.y, hitBoxes);
        }

        const resizeEntity = this.queries.resizeOperation.query!.single();
        const resizeComponent = resizeEntity?.getComponent<ResizeComponent>(ComponentId.Resize);
        const grabberEntities = this.queries.grabbers.query!.entities;

        const visibleRanges = getTokenRangesForPages(this.resources.get("visiblePages").value);
        const tokenRange = selectedEntity.getComponent<TokenRangeComponent>(ComponentId.TokenRange)!.tokenRange;

        const isWithinRange = (token: number, range: TokenRange) => token >= range.fromTok && token <= range.toTok;

        const disabledGrabbers = this.getDisabledGrabberTypes(tokenRange);
        const hideGrabbers = this.resources.get("resizeDisabled").value || !!resizeComponent?.isCreating || !!resizeComponent?.isResizing || !isHovered;

        for (const [entity, grabber, interactable] of grabberEntities.map((e): [Entity, GrabberComponent, InteractableComponent] => [e, e.getComponent<GrabberComponent>(ComponentId.Grabber)!, e.getComponent<InteractableComponent>(ComponentId.Interactable)!])) {
            let isHidden = hideGrabbers || disabledGrabbers.includes(grabber.grabber.type);
            if (!isHidden) {
                switch (grabber.grabber.type) {
                    case GrabberType.Left:
                    case GrabberType.Top:
                        if (!visibleRanges.find(range => isWithinRange(tokenRange.fromTok + 1, range))) {
                            isHidden = true;
                        }
                        break;
                    case GrabberType.Right:
                    case GrabberType.Bottom:
                        if (!visibleRanges.find(range => isWithinRange(tokenRange.toTok - 1, range))) {
                            isHidden = true;
                        }
                }
            }

            if (isHidden) {
                entity.removeComponent(ComponentId.Renderable);
            }
            else {
                entity.ensureComponent(tagComponent(ComponentId.Renderable));
            }

            interactable.disabled = isHidden;
        }
    }

    private getDisabledGrabberTypes(range: TokenRange): GrabberType[] {
        const disabled: GrabberType[] = [];
        for (const tableCellEntity of this.queries.tableCells.query!.entities) {
            const tokenRange = tableCellEntity.getComponent<TokenRangeComponent>(ComponentId.TokenRange)!.tokenRange;
            const fromTokContained = range.fromTok >= tokenRange.fromTok && range.fromTok < tokenRange.toTok;
            const toTokContained = range.toTok - 1 >= tokenRange.fromTok && range.toTok <= tokenRange.toTok;

            if (fromTokContained !== toTokContained) {
                if (fromTokContained) {
                    disabled.push(GrabberType.Left);
                }
                else {
                    disabled.push(GrabberType.Right);
                }
            }
        }

        return disabled;
    }

    // Handle resizing end in the multi selection mode
    // Multiselect allows active resize operations in selection mode
    private handleMultiSelect() {
        if (!this.queries.resizeOperation.query || !this.resources.get('multiSelect').value || this.resources.get('mode').value !== Mode.Edit) {
            return;
        }

        const resize = this.queries.resizeOperation.query!.single()?.getComponent<ResizeComponent>(ComponentId.Resize)!;
        if (resize?.isResizing === false) {
            this.resources.set('mode', Mode.Selection);
        }
    }

    // Handle the selected instance changing while there is an active resize operation
    private handleSelectionChanges() {
        const selectionChanged = this.resources.get('selectedEntityName').updated;
        if (!this.queries.resizeOperation.query || !selectionChanged) {
            return;
        }

        const resize = this.queries.resizeOperation.query!.single();
        const selectedEntity = this.queries.selected.query!.single();
        if (!resize) {
            return;
        }

        // Handle selection changes
        if (resize.id !== selectedEntity?.id) {
            // end resize for this entity
            if (resize.hasComponent(ComponentId.Bounds)) {
                const bounds = resize.getComponent<BoundsComponent>(ComponentId.Bounds)!;
                if (resize.hasComponent(ComponentId.ChunkRender)) {
                    const chunkRenderInfo = resize.getComponent<ChunkRenderComponent>(ComponentId.ChunkRender)!;
                    bounds.bounds = chunkRenderInfo.bounds;
                }
            }

            resize.removeComponent(ComponentId.Resize);
        }
    }

    private endResize(succeeded: boolean) {
        if (!this.queries.resizeOperation.query) {
            return;
        }

        for (const resize of this.queries.resizeOperation.query!.entities) {
            if (!succeeded) {
                const tokenRange = resize.getComponent<TokenRangeComponent>(ComponentId.TokenRange)!;
                const resizeComponent = resize.getComponent<ResizeComponent>(ComponentId.Resize)!;
                tokenRange.tokenRange = resizeComponent.ops![0].tokenRange;
            }
            else {
                // Apply the new bounds if possible - these are simply optimisted UX updates. The real bounds will get
                // generated when the DGML update re-generates the control.
                if (resize.hasComponent(ComponentId.Bounds)) {
                    const bounds = resize.getComponent<BoundsComponent>(ComponentId.Bounds)!;
                    if (resize.hasComponent(ComponentId.ChunkRender)) {
                        const chunkRenderInfo = resize.getComponent<ChunkRenderComponent>(ComponentId.ChunkRender)!;
                        bounds.bounds = chunkRenderInfo.bounds;
                    }
                }
            }

            resize.removeComponent(ComponentId.Resize);
        }

        this.resources.set('mode', Mode.Selection);
    }

    private resetResizeVisuals() {
        const resizeComp = this.queries.resizeOperation.query!.single()?.getComponent<ResizeComponent>(ComponentId.Resize)!;
        if (!!resizeComp) {
            delete resizeComp.primaryColor;
            delete resizeComp.secondaryColor;
        }
    }

    private updateBreakHyperlineVisuals() {
        const resizeComp = this.queries.resizeOperation.query!.single()?.getComponent<ResizeComponent>(ComponentId.Resize)!;
        if (!resizeComp?.isResizing || resizeComp.snapBehavior !== TokenSnapBehavior.TokenLine) {
            return;
        }

        const breakHyperlineComp = this.queries.breakHyperline.query!.single()?.getComponent<BreakHyperlineComponent>(ComponentId.BreakHyperline)!;
        if (!breakHyperlineComp) {
            return
        }

        const settings = breakHyperlineComp.settings;
        let currentAnimationTime = breakHyperlineComp.currentAnimationTime ?? 0;
        currentAnimationTime += this.dt * 1000;

        const totalAnimationDurationMs = settings.pulseEnterDurationMs + settings.pulseHoldDurationMs + settings.pulseExitDurationMs + settings.pulseIntervalDurationMs;
        while (currentAnimationTime > totalAnimationDurationMs) {
            currentAnimationTime -= totalAnimationDurationMs;
        }

        currentAnimationTime = clamp(currentAnimationTime, 0, totalAnimationDurationMs);

        const intervalStartTimeMs = settings.pulseEnterDurationMs + settings.pulseHoldDurationMs + settings.pulseExitDurationMs;
        const pulseExitStartTimeMs = settings.pulseEnterDurationMs + settings.pulseHoldDurationMs;
        const pulseHoldStartTimeMs = settings.pulseEnterDurationMs;

        let targetColor = settings.defaultColor;
        if (currentAnimationTime >= intervalStartTimeMs) {
            // Waiting for next pulse, use default color
            targetColor = settings.defaultColor;

        }
        else if (currentAnimationTime >= pulseExitStartTimeMs) {
            // Pulse exit animation
            const dt = (currentAnimationTime - pulseExitStartTimeMs) / settings.pulseExitDurationMs;
            const animationCurve = animationCurves[settings.pulseEnterAnimationCurve];
            const color = blendColors(settings.pulseColor, settings.defaultColor, animationCurve(dt));
            targetColor = color.toHexString();

        }
        else if (currentAnimationTime >= pulseHoldStartTimeMs) {
            // Show pulse color
            targetColor = settings.pulseColor;
        }
        else {
            // Pulse enter animation
            const dt = currentAnimationTime / settings.pulseEnterDurationMs;
            const animationCurve = animationCurves[settings.pulseEnterAnimationCurve];
            const color = blendColors(settings.defaultColor, settings.pulseColor, animationCurve(dt));
            targetColor = color.toHexString();
        }

        breakHyperlineComp.currentAnimationTime = currentAnimationTime;
        resizeComp.primaryColor = targetColor;
    }

    private getResizeRestrictedRange(resizeEntity: Entity): TokenRange | null {
        const resizeRange = resizeEntity.getComponent<TokenRangeComponent>(ComponentId.TokenRange)?.tokenRange;
        if (!resizeRange) {
            return null;
        }

        let restrictedRange = this.getResizeRestrictedRangeForControl(resizeEntity);
        const rootControlEntity = this.queries.root.query!.single();
        const rootTokenRange = rootControlEntity?.getComponent<TokenRangeComponent>(ComponentId.TokenRange)!.tokenRange;
        if (!rootTokenRange || this.queries.multiSelect.query!.entities.length <= 1) {
            return restrictedRange;
        }

        restrictedRange ??= { fromTok: rootTokenRange.fromTok, toTok: rootTokenRange.toTok };

        for (const entity of this.queries.multiSelect.query!.entities) {
            const tokenRange = entity.getComponent<TokenRangeComponent>(ComponentId.TokenRange)!.tokenRange;
            if (!tokenRange || entity === resizeEntity || TokenRangeUtils.rangesEqual(tokenRange, resizeRange)) {
                continue;
            }

            if (tokenRange.toTok <= resizeRange.fromTok && tokenRange.toTok > restrictedRange.fromTok) {
                restrictedRange.fromTok = tokenRange.toTok;
            }

            if (tokenRange.fromTok >= resizeRange.toTok && tokenRange.fromTok < restrictedRange.toTok) {
                restrictedRange.toTok = tokenRange.fromTok;
            }
        }

        return restrictedRange;
    }

    // Gets the restricted resize range for a control entity by walking the control tree for structural boundaries
    private getResizeRestrictedRangeForControl(controlEntity: Entity): TokenRange | null {
        if (!controlEntity.hasComponent(ComponentId.Control)) {
            return null;
        }

        const tokenRange = controlEntity.getComponent<TokenRangeComponent>(ComponentId.TokenRange)!.tokenRange;
        let restrictedRange: TokenRange | null = null;

        const rootControlEntity = this.queries.root.query!.single();
        if (!!rootControlEntity) {
            let lastRestrictedControl: ControlComponent | null = null;

            // Walk the tree in level order to find the location of the current range within the root,
            // and find any restrictive resize boundaries like hyperlines and table cells
            const iterator = levelOrderTraversal(rootControlEntity, this.entities, e => {
                const control = e.getComponent<ControlComponent>(ComponentId.Control)!;
                return control.type !== ControlType.SmallChunk && TokenRangeUtils.rangeIsContained(tokenRange, getTokenRangeForControl(control));
            });

            let current = iterator.next();
            while (!!current.value) {
                const control = current.value.getComponent<ControlComponent>(ComponentId.Control)!;
                if (!!control.isRoot && !lastRestrictedControl) {
                    // Ensure that we restrict resizing to the root node
                    lastRestrictedControl = control;
                }
                if (control.type === ControlType.TableCell) {
                    lastRestrictedControl = control;
                }

                current = iterator.next();
            }

            if (!!lastRestrictedControl) {
                restrictedRange = {
                    fromTok: lastRestrictedControl.fromTok,
                    toTok: lastRestrictedControl.toTok
                };
            }
        }

        return restrictedRange;
    }

    private getTokenLineForCursor(isFromTok: boolean): TokenRangeBox | null {
        const cursorComp = this.queries.cursor.query!.single()?.getComponent<CursorComponent>(ComponentId.Cursor)!;
        const tokenLinesComp = this.queries.tokenLines.query!.single()?.getComponent<TokenLinesComponent>(ComponentId.TokenLines)!;

        // Ensure the cursor is valid and within the bounds of a visible page
        let tokenLine: TokenRangeBox | null = null;
        if (!!cursorComp?.currentPageId && !!cursorComp.tokenLine && !!cursorComp.point) {
            // Retrieve the token lines for the page that the cursor is on
            const tokenLines = tokenLinesComp.tokenLines[cursorComp.currentPageId];

            let targetIndex = cursorComp.tokenLine.index;
            tokenLine = tokenLines[targetIndex];
            while (((isFromTok && cursorComp.point.y > tokenLine.box.top) || (!isFromTok && cursorComp.point.y < tokenLine.box.bottom)) &&
                !(cursorComp.point.y >= tokenLine.box.top && cursorComp.point.y <= tokenLine.box.bottom) &&
                !!tokenLines[targetIndex]) {

                tokenLine = tokenLines[targetIndex];
                targetIndex = isFromTok ? targetIndex + 1 : targetIndex - 1;
            }
        }

        return tokenLine;
    }

    private getTokenForCursorFromLine(line: TokenRangeBox, isFromTok: boolean): number | null {
        const cursorComp = this.queries.cursor.query!.single()?.getComponent<CursorComponent>(ComponentId.Cursor)!;
        if (!!cursorComp.point && !!cursorComp.tokenLine) {
            const cursor = cursorComp.point;
            if ((isFromTok && (cursor.y <= line.box.top || cursor.x < line.box.left)) ||
                (!isFromTok && (cursor.y >= line.box.bottom || cursor.x > line.box.right))) {
                // Include the entire line
                return isFromTok ? line.tokenRange.fromTok : line.tokenRange.toTok - 1;
            }
            else {
                return cursorComp.token ?? null
            }
        }

        return null;
    }
}
